import {useQuery, type QueryKey} from '@tanstack/react-query'
import {useCallback, useMemo} from 'react'
import {useServices} from '@/services/context'
import type {Party, PartyRelationship} from '@/types/party'
import type {IdentityListItem} from '@/types/identity'

export const PARTY_KEY = 'party' as const
export const PARTIES_KEY = 'parties' as const

export const PARTY_SUBKEY_IDENTITIES = 'identities-by-party' as const
export const PARTY_SUBKEY_PARENT_RELATIONSHIPS = 'party-relationships-from' as const
export const PARTY_SUBKEY_CHILD_RELATIONSHIPS = 'party-relationships-to' as const
export const PARTY_SUBKEY_ANCESTORS = 'party-ancestors' as const

export interface PartyAncestor {
    id: string
    derivedDisplayName: string
    partyType: string
}

export interface UsePartyDetailResult {
    party: Party | undefined
    identities: IdentityListItem[]
    parentRelationships: PartyRelationship[]
    childRelationships: PartyRelationship[]
    ancestors: PartyAncestor[]
    isLoading: boolean
    error: Error | null
    queryKeyFor: (sub?: string) => QueryKey
}

/**
 * Composite read hook for the PartyDetail page.
 *
 * Bundles the five queries the detail body relies on so the page never has to
 * derive query keys inline. Returns the bare slices the page renders plus the
 * loading / error flags for the primary party fetch — ancillary slices fall
 * back to empty arrays by design (the page renders an empty section in that
 * case, not a spinner).
 *
 * Mirrors the shape of {@code useIdentity} from 607.01 — exposes
 * {@code queryKeyFor} so downstream consumers (e.g. {@code SourcesCard},
 * {@code InlineAttributeEditor}) can invalidate sub-keys without having to
 * know the literal key strings.
 *
 * Pass {@code id} as {@code undefined} during the brief gap before
 * {@code useParams} resolves to keep the hook safe under test renders.
 */
export function usePartyDetail(id: string | undefined): UsePartyDetailResult {
    const services = useServices()

    const queryKeyFor = useCallback((sub?: string): QueryKey => {
        return sub ? [PARTY_KEY, id, sub] : [PARTY_KEY, id]
    }, [id])

    const partyQuery = useQuery<Party>({
        queryKey: [PARTY_KEY, id],
        queryFn: () => services.parties.detail(id!),
        enabled: !!id,
    })

    const identitiesQuery = useQuery<IdentityListItem[]>({
        queryKey: [PARTY_SUBKEY_IDENTITIES, id],
        queryFn: () => services.identities.byParty(id!),
        enabled: !!id,
    })

    const parentRelationshipsQuery = useQuery<PartyRelationship[]>({
        queryKey: [PARTY_SUBKEY_PARENT_RELATIONSHIPS, id],
        queryFn: () => services.partyRelationships.fromParty(id!),
        enabled: !!id,
    })

    const childRelationshipsQuery = useQuery<PartyRelationship[]>({
        queryKey: [PARTY_SUBKEY_CHILD_RELATIONSHIPS, id],
        queryFn: () => services.partyRelationships.toParty(id!),
        enabled: !!id,
    })

    const ancestorsQuery = useQuery<PartyAncestor[]>({
        queryKey: [PARTY_SUBKEY_ANCESTORS, id],
        queryFn: () => services.parties.ancestors(id!),
        enabled: !!id,
    })

    return useMemo(() => ({
        party: partyQuery.data,
        identities: identitiesQuery.data ?? [],
        parentRelationships: parentRelationshipsQuery.data ?? [],
        childRelationships: childRelationshipsQuery.data ?? [],
        ancestors: ancestorsQuery.data ?? [],
        isLoading: partyQuery.isLoading,
        error: (partyQuery.error as Error | null) ?? null,
        queryKeyFor,
    }), [
        partyQuery.data,
        partyQuery.isLoading,
        partyQuery.error,
        identitiesQuery.data,
        parentRelationshipsQuery.data,
        childRelationshipsQuery.data,
        ancestorsQuery.data,
        queryKeyFor,
    ])
}
